import { useEffect, useState, useRef, useCallback } from "react";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { LogOut, Upload, Video, Loader2, Plus, Trash2, ShieldCheck, Play, Camera, Mic, FileText, RefreshCw } from "lucide-react";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { brandify } from "../lib/brand";

const TIER_TONE = {
  "Applicant":"bg-slate-100 text-slate-700 border-slate-200",
  "StaffHub Qualified":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "StaffHub Premier":"bg-amber-50 text-amber-800 border-amber-300",
};
const STATUS_TONE = {
  "Submitted":"bg-sky-50 text-sky-700 border-sky-200",
  "Approved":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Kept Internal":"bg-slate-100 text-slate-600 border-slate-200",
  "New Response Requested":"bg-amber-50 text-amber-800 border-amber-300",
};
const CAREER_QS = [
  ["work_looking_for", "What kind of work are you looking for?"],
  ["work_passionate", "What work are you most passionate about?"],
  ["exceptionally_good_at", "What are you exceptionally good at?"],
  ["top_accomplishments", "What are your three strongest professional accomplishments?"],
  ["do_better_than_most", "What can you do better than most people in your field?"],
  ["unique_value", "What unique value do you bring to an employer?"],
  ["why_choose_you", "Why should a company hire you over another equally qualified candidate?"],
  ["ideal_environment", "Describe your ideal work environment."],
  ["ideal_manager", "What type of manager/team helps you perform your best?"],
  ["work_avoid", "What type of work do you NOT want to do?"],
  ["career_goals", "What are your 2-5 year career goals?"],
];

const ROLE_FAMILIES = ["Sales / Appointment Setting", "Customer Service / Front Desk", "Administrative", "Technical / Programming"];

export default function CandidatePortal() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [media, setMedia] = useState([]);
  const [auditionQs, setAuditionQs] = useState([]);
  const [tab, setTab] = useState("profile");
  const [imported, setImported] = useState(null);
  const [busy, setBusy] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    try {
      const [{data:m},{data:a},{data:i},{data:mm}] = await Promise.all([
        api.get("/candidate-portal/me"),
        api.get("/assessments"), api.get("/interviews"),
        api.get("/candidate-portal/media"),
      ]);
      setMe(m); setAssessments(a); setInterviews(i); setMedia(mm);
    } catch (e) {
      console.error("CandidatePortal load failed", e);
      toast.error("Failed to load your portal. Please refresh.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!me) return;
    api.get("/audition-questions")
      .then(r => setAuditionQs(r.data))
      .catch(e => console.error("audition-questions load failed", e));
  }, [me?.id]);
  if (!me) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading...</div>;

  const patch = async (fields) => {
    try { const { data } = await api.patch("/candidate-portal/me", fields); setMe(data); toast.success("Saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const uploadResume = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/candidate-portal/resume/parse", fd, {headers:{"Content-Type":"multipart/form-data"}});
      setImported(data); toast.success("Parsed - review before applying");
    } catch (e) { toast.error(e.response?.data?.detail || "Parsing failed"); }
    finally { setBusy(false); e.target.value=""; }
  };
  const applyImported = async () => {
    const {imported_unverified, imported_at, ...flat} = imported || {};
    await patch(flat); setImported(null);
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/candidate-portal/video/upload", fd, {headers:{"Content-Type":"multipart/form-data"}});
      toast.success(`Uploaded (${Math.round(data.size/1024/1024)}MB)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value=""; }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5*1024*1024) { toast.error("Photo too large (max 5 MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/candidate-portal/photo/upload", fd, {headers:{"Content-Type":"multipart/form-data"}});
      toast.success(`Photo uploaded (${Math.round(data.size/1024)} KB)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value=""; }
  };

  const uploadMedia = async (file, media_type, opts={}) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const qs = new URLSearchParams({media_type, ...(opts.question_id?{question_id:opts.question_id}:{}), ...(opts.title?{title:opts.title}:{}), ...(opts.role_family?{role_family:opts.role_family}:{})});
      const { data } = await api.post(`/candidate-portal/media/upload?${qs.toString()}`, fd, {headers:{"Content-Type":"multipart/form-data"}});
      toast.success("Uploaded"); await load(); return data;
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  // Multi-entry helpers
  const List = ({field, label, columns, defaults}) => {
    const items = me[field] || [];
    const setItems = (arr) => patch({[field]: arr});
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
          <button data-testid={`add-${field}`} onClick={()=>setItems([...items, {...defaults, _id: Date.now()}])} className="text-xs font-semibold text-sky-700 inline-flex items-center gap-1"><Plus size={12}/> Add</button>
        </div>
        {items.length === 0 && <div className="text-xs text-slate-500">None yet. Click Add to start.</div>}
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={it._id ?? idx} className="border border-slate-200 rounded-md p-3 relative">
              <button onClick={()=>setItems(items.filter((_,i)=>i!==idx))} className="absolute top-2 right-2 text-slate-400 hover:text-red-600" data-testid={`del-${field}-${idx}`}><Trash2 size={12}/></button>
              <div className="grid grid-cols-2 gap-3">
                {columns.map(([k, cl, type, opts]) => (
                  <div key={k} className={cl || ""}>
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{k.replace(/_/g," ")}</label>
                    {type === "textarea" ?
                      <textarea rows={2} defaultValue={it[k]||""} onBlur={e=>{const next=[...items];next[idx]={...it,[k]:e.target.value};setItems(next);}}
                        className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/> :
                     type === "checkbox" ?
                      <input type="checkbox" checked={!!it[k]} onChange={e=>{const next=[...items];next[idx]={...it,[k]:e.target.checked};setItems(next);}}/> :
                     type === "select" ?
                      <select defaultValue={it[k]||""} onBlur={e=>{const next=[...items];next[idx]={...it,[k]:e.target.value};setItems(next);}} className="w-full h-9 px-3 rounded border border-slate-300 text-sm">
                        <option value="">-</option>{opts.map(o=><option key={o}>{o}</option>)}
                      </select> :
                      <input type={type||"text"} defaultValue={it[k]||""} onBlur={e=>{const next=[...items];next[idx]={...it,[k]:e.target.value};setItems(next);}}
                        className="w-full h-9 px-3 rounded border border-slate-300 text-sm"/>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center"><InfinityMark size={22}/></div>
            <div><div className="font-display font-bold text-sm">Connect<span className="text-teal-300">My</span>Hub<span className="text-teal-300 text-[10px] font-semibold ml-0.5">™</span></div><div className="text-[10px] text-slate-400 uppercase tracking-widest">Talent Portal</div></div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right"><div className="text-xs font-semibold">{user?.name}</div><div className="text-[10px] text-slate-400">{user?.email}</div></div>
            <button data-testid="portal-logout" onClick={logout} className="text-xs flex items-center gap-1 border border-slate-700 rounded-md px-3 py-1.5"><LogOut size={12}/> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-6 flex flex-wrap items-end gap-4 justify-between">
          <div className="flex items-center gap-4">
            {me.photo_url ? (
              <img src={`${API_BASE.replace(/\/api$/,"")}${me.photo_url}`} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" data-testid="portal-photo"/>
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center text-slate-400"><Camera size={20}/></div>
            )}
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Welcome, {me.first_name}</div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Your ConnectMyHub profile</h1>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <span data-testid="tier-badge" className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded border ${TIER_TONE[me.qualification_tier] || TIER_TONE.Applicant}`}><ShieldCheck size={11}/> {me.qualification_tier}</span>
                <div className="text-xs text-slate-500">Profile {me.profile_complete_pct || 0}% complete</div>
                <div className="flex-1 max-w-[180px] h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-sky-500" style={{width: `${me.profile_complete_pct || 0}%`}}></div></div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-slate-200 mb-6 flex gap-1 overflow-x-auto">
          {[["profile","Basics"],["photo","Photo"],["career","Career Profile"],["experience","Experience"],["education","Education"],["certifications","Certifications"],["skills","Skills & Languages"],["links","Links"],["references","References"],["resume","Resume Import"],["media","Media & Auditions"],["english","English Assessment"],["assessments","Assessments"],["interviews","Interviews"]].map(([id,lbl]) => (
            <button key={id} data-testid={`tab-${id}`} onClick={()=>setTab(id)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 ${tab===id?"border-sky-600 text-slate-900":"border-transparent text-slate-500"}`}>{lbl}</button>
          ))}
        </div>

        {tab === "profile" && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="First name" value={me.first_name} onSave={v=>patch({first_name:v})}/>
            <Field label="Last name" value={me.last_name} onSave={v=>patch({last_name:v})}/>
            <Field label="Country" value={me.country} onSave={v=>patch({country:v})}/>
            <Field label="City" value={me.city} onSave={v=>patch({city:v})}/>
            <Field label="Primary role" value={me.primary_role} onSave={v=>patch({primary_role:v})}/>
            <Field label="Seniority" value={me.seniority} onSave={v=>patch({seniority:v})} placeholder="Junior/Mid/Senior/Manager/Director"/>
            <Field label="Headline" value={me.headline} onSave={v=>patch({headline:v})} className="md:col-span-2"/>
            <Field label="Summary" value={me.summary} onSave={v=>patch({summary:v})} textarea className="md:col-span-2"/>
            <Field label="Years experience" value={me.years_experience} type="number" onSave={v=>patch({years_experience:parseInt(v||0)})}/>
            <Field label="Desired comp (USD/mo)" value={me.desired_compensation} type="number" onSave={v=>patch({desired_compensation:parseFloat(v||0)})}/>
          </div>
        )}

        {tab === "photo" && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-w-2xl">
            <div className="flex items-center gap-2 mb-3"><Camera size={16} className="text-sky-600"/><h3 className="font-semibold text-slate-900">Professional photo</h3></div>
            <p className="text-xs text-slate-500 mb-4">Upload a clear, professional headshot (JPG, PNG or WebP, max 5 MB). Employers see this on your candidate profile. ConnectMyHub does not analyze attractiveness, age, race, ethnicity or any protected characteristic.</p>
            <div className="flex items-center gap-6">
              <div className="w-40 h-40 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                {me.photo_url ? <img src={`${API_BASE.replace(/\/api$/,"")}${me.photo_url}`} alt="" className="w-full h-full object-cover" data-testid="photo-preview"/> : <Camera size={32} className="text-slate-400"/>}
              </div>
              <div className="flex-1">
                <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold cursor-pointer" data-testid="photo-upload-label">
                  <Upload size={13}/> {me.photo_url?"Replace photo":"Upload photo"}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" onChange={uploadPhoto} data-testid="photo-upload" className="hidden"/>
                </label>
                {busy && <div className="mt-2 text-xs flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Uploading…</div>}
                {me.photo_url && <div className="mt-2 text-[11px] text-emerald-700">Photo uploaded {me.photo_uploaded_at ? `on ${new Date(me.photo_uploaded_at).toLocaleDateString()}` : ""}.</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "career" && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <p className="text-xs text-slate-500 mb-4">Answers are stored as structured career profile data and are used by ConnectMyHub recruiters and the AI matching engine.</p>
            <div className="space-y-4">
              {CAREER_QS.map(([k, q]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">{q}</label>
                  <textarea data-testid={`career-${k}`} rows={2} defaultValue={me.career_profile?.[k] || ""}
                    onBlur={e=>patch({career_profile: {...(me.career_profile||{}), [k]: e.target.value}})}
                    className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none focus:border-sky-500 outline-none"/>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "experience" && <List field="employment_history" label="Employment history" defaults={{current:false}}
          columns={[["employer"],["job_title"],["industry"],["location"],["start_date","",""],["end_date","",""],["current","","checkbox"],["team_size","","number"],["responsibilities","col-span-2","textarea"],["accomplishments","col-span-2","textarea"],["quantifiable_results","col-span-2","textarea"],["management_responsibilities","col-span-2","textarea"]]}/>}

        {tab === "education" && <List field="education_history" label="Education" defaults={{}}
          columns={[["institution"],["degree_level","","select",["High School","Associate","Bachelor","Master","Doctorate","Other"]],["degree"],["field"],["graduation_date"],["honors"]]}/>}

        {tab === "certifications" && <List field="certifications_detailed" label="Certifications & Licenses" defaults={{}}
          columns={[["name"],["issuing_organization"],["issue_date"],["expiration_date"],["credential_id","col-span-2"]]}/>}

        {tab === "skills" && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Skills</h3>
              <Field label="Professional skills (comma-separated)" value={(me.skills||[]).join(", ")} onSave={v=>patch({skills:v.split(",").map(s=>s.trim()).filter(Boolean)})}/>
              <Field label="Software / platforms" value={(me.software_skills||[]).join(", ")} onSave={v=>patch({software_skills:v.split(",").map(s=>s.trim()).filter(Boolean)})} className="mt-3"/>
              <Field label="CRM systems" value={(me.crm_experience||[]).join(", ")} onSave={v=>patch({crm_experience:v.split(",").map(s=>s.trim()).filter(Boolean)})} className="mt-3"/>
              <Field label="AI tools" value={(me.ai_tools||[]).join(", ")} onSave={v=>patch({ai_tools:v.split(",").map(s=>s.trim()).filter(Boolean)})} className="mt-3"/>
              <Field label="Industry-specific systems" value={(me.industry_systems||[]).join(", ")} onSave={v=>patch({industry_systems:v.split(",").map(s=>s.trim()).filter(Boolean)})} className="mt-3"/>
            </div>
            <List field="languages_detailed" label="Languages" defaults={{primary:false}}
              columns={[["language"],["spoken_proficiency","","select",["Basic","Conversational","Professional","Fluent","Native"]],["written_proficiency","","select",["Basic","Conversational","Professional","Fluent","Native"]],["primary","","checkbox"]]}/>
          </div>
        )}

        {tab === "links" && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="LinkedIn URL" value={me.linkedin_url} onSave={v=>patch({linkedin_url:v})}/>
            <Field label="Portfolio URL" value={me.portfolio_url} onSave={v=>patch({portfolio_url:v})}/>
            <Field label="GitHub URL" value={me.github_url} onSave={v=>patch({github_url:v})}/>
            <Field label="Personal website" value={me.website_url} onSave={v=>patch({website_url:v})}/>
            <Field label="Work samples (comma-separated URLs)" value={(me.work_samples||[]).join(", ")} onSave={v=>patch({work_samples:v.split(",").map(s=>s.trim()).filter(Boolean)})} className="md:col-span-2"/>
          </div>
        )}

        {tab === "references" && <List field="references" label="References / Recommendations" defaults={{permission_to_contact:false}}
          columns={[["name"],["company"],["job_title"],["relationship"],["email"],["phone"],["permission_to_contact","","checkbox"],["reference_notes","col-span-2","textarea"]]}/>}

        {tab === "resume" && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4"><Upload size={16} className="text-sky-600"/><h3 className="font-semibold text-slate-900">Upload resume (PDF or DOCX)</h3></div>
            <p className="text-xs text-slate-500 mb-4">AI extracts structured data. Nothing is saved until you review and apply. Fields you don't confirm are discarded.</p>
            <input type="file" accept=".pdf,.docx" onChange={uploadResume} data-testid="resume-upload" className="text-sm"/>
            {busy && <div className="mt-3 text-xs flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Parsing...</div>}
            {imported && (
              <div className="mt-6 border border-amber-300 bg-amber-50 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-900 mb-2">Imported (unverified - review before applying)</div>
                <pre className="text-[11px] whitespace-pre-wrap max-h-96 overflow-auto font-mono">{JSON.stringify(imported, null, 2)}</pre>
                <div className="mt-3 flex gap-2">
                  <button data-testid="apply-imported" onClick={applyImported} className="h-9 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold">Apply to my profile</button>
                  <button onClick={()=>setImported(null)} className="h-9 px-4 rounded-md border border-slate-300 text-sm">Discard</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "media" && (
          <MediaAuditions me={me} media={media} auditionQs={auditionQs} onUpload={uploadMedia}
                          uploadIntro={uploadVideo} backendUrl={backendUrl} busy={busy} onReload={load}/>
        )}

        {tab === "english" && <EnglishAssessment me={me} onSubmitted={load}/>}

        {tab === "assessments" && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {assessments.length === 0 ? <div className="p-6 text-sm text-slate-500">No assessments yet.</div> :
              <table className="w-full text-sm">
                <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Score</th><th className="text-left px-4 py-3">Result</th></tr></thead>
                <tbody>{assessments.map(a => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-semibold">{a.name}</td>
                    <td className="px-4 py-2.5">{a.type}</td>
                    <td className="px-4 py-2.5 font-mono">{a.score != null ? `${a.score}/${a.max_score}` : "-"}</td>
                    <td className="px-4 py-2.5">{a.passed === true ? <span className="text-emerald-700">Passed</span> : a.passed === false ? <span className="text-red-700">Not passed</span> : "-"}</td>
                  </tr>
                ))}</tbody>
              </table>}
          </div>
        )}

        {tab === "interviews" && (
          <div className="space-y-3" data-testid="candidate-interviews-list">
            {interviews.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 bg-white border border-slate-200 rounded-xl">No interview requests yet.</div>
            ) : (
              interviews.map(i => (
                <InterviewRequestCard key={i.id} interview={i} onUpdate={load}/>
              ))
            )}
          </div>
        )}
      </main>
      <Toaster position="top-right" richColors/>
    </div>
  );
}

function InterviewRequestCard({interview: i, onUpdate}) {
  const [showAvail, setShowAvail] = useState(false);
  const [windows, setWindows] = useState(["", "", ""]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = i.status === "Requested" && i.candidate_interested === undefined;
  const responded = i.candidate_interested !== undefined && i.candidate_interested !== null;

  const respond = async (interested) => {
    setBusy(true);
    try {
      const payload = {interview_id: i.id, interested,
        available_windows: interested ? windows.filter(w=>w.trim()) : [],
        notes: notes || null};
      await api.post(`/interviews/${i.id}/interest`, payload);
      toast.success(interested ? "Interest confirmed — we'll schedule shortly" : "Response recorded");
      setShowAvail(false); await onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to respond"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`iv-req-${i.id}`} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">{i.interview_type || "Interview Request"}</div>
          <div className="font-display text-lg font-bold text-slate-900">{i.status}</div>
          <div className="text-xs text-slate-500 mt-1">
            {i.scheduled_at ? `Proposed: ${new Date(i.scheduled_at).toLocaleString()}` : "Date TBD"}
            {i.time_zone ? ` · ${i.time_zone}` : ""}
          </div>
          {i.meeting_link && <a href={i.meeting_link} target="_blank" rel="noreferrer" className="text-sky-700 text-xs font-semibold mt-1 inline-block">Meeting link</a>}
        </div>
        <div className="flex flex-col gap-2 items-end">
          {responded ? (
            <span data-testid={`iv-responded-${i.id}`} className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${i.candidate_interested?"bg-emerald-50 text-emerald-700 border-emerald-300":"bg-rose-50 text-rose-700 border-rose-300"}`}>
              {i.candidate_interested ? "Interested" : "Declined"}
            </span>
          ) : pending ? (
            <div className="flex gap-2">
              <button data-testid={`iv-accept-${i.id}`} onClick={()=>setShowAvail(true)} disabled={busy} className="h-9 px-3 rounded bg-emerald-600 text-white text-xs font-semibold">Interested</button>
              <button data-testid={`iv-decline-${i.id}`} onClick={()=>respond(false)} disabled={busy} className="h-9 px-3 rounded border border-slate-300 text-slate-700 text-xs font-semibold">Decline</button>
            </div>
          ) : null}
        </div>
      </div>
      {showAvail && (
        <div data-testid={`iv-avail-${i.id}`} className="mt-4 border-t border-slate-100 pt-4">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-2">Share up to 3 time windows that work for you</div>
          <div className="grid md:grid-cols-3 gap-2">
            {[0,1,2].map(idx => (
              <input key={idx} data-testid={`iv-win-${i.id}-${idx}`} value={windows[idx]} onChange={e=>{const n=[...windows]; n[idx]=e.target.value; setWindows(n);}}
                     placeholder={`e.g. Tue 2-4pm ${idx===0?"ET":""}`}
                     className="h-9 px-3 rounded border border-slate-300 text-sm"/>
            ))}
          </div>
          <textarea data-testid={`iv-notes-${i.id}`} value={notes} onChange={e=>setNotes(e.target.value)} rows={2} placeholder="Optional notes for the employer"
                    className="mt-2 w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={()=>setShowAvail(false)} className="h-9 px-3 rounded border border-slate-300 text-slate-700 text-xs font-semibold">Cancel</button>
            <button data-testid={`iv-submit-${i.id}`} onClick={()=>respond(true)} disabled={busy || !windows.filter(w=>w.trim()).length} className="h-9 px-3 rounded bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">Confirm interest</button>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({label, value, onSave, textarea, type, placeholder, className=""}) => {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <div className={className}>
      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      {textarea ?
        <textarea rows={3} value={v} placeholder={placeholder} onChange={e=>setV(e.target.value)} onBlur={()=>v !== (value ?? "") && onSave(v)} className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm resize-none focus:border-sky-500 outline-none"/> :
        <input type={type||"text"} value={v} placeholder={placeholder} onChange={e=>setV(e.target.value)} onBlur={()=>String(v) !== String(value ?? "") && onSave(v)} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-sky-500 outline-none"/>}
    </div>
  );
};

const EN_SECTIONS = [["spoken_clarity","Spoken clarity / comprehensibility",20],["listening","Listening / comprehension",15],["grammar","Grammar / vocabulary",15],["conversational","Natural conversational ability",15],["professional","Professional communication",15],["written","Written English",10],["confidence","Confidence / presentation",10]];
const EnglishAssessment = ({me, onSubmitted}) => {
  const [scores, setScores] = useState(Object.fromEntries(EN_SECTIONS.map(s=>[s[0], me.english_assessment?.[s[0]] ?? 0])));
  const [busy, setBusy] = useState(false);
  const total = EN_SECTIONS.reduce((sum, [k]) => sum + (parseInt(scores[k])||0), 0);
  const max = EN_SECTIONS.reduce((sum, [,,mx]) => sum + mx, 0);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch("/candidate-portal/me", {english_assessment: scores, english_written: Math.min(5, Math.round((scores.written / 10) * 5)), english_spoken: Math.min(5, Math.round(((scores.spoken_clarity + scores.conversational + scores.professional) / 50) * 5))});
      toast.success(`Submitted (${total}/${max})`);
      onSubmitted?.();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900 mb-1">ConnectMyHub English / Communication self-assessment</h3>
      <p className="text-xs text-slate-500 mb-4">Rate yourself honestly - a recruiter will verify during your screening interview. Individual per-candidate scoring only; ConnectMyHub never uses country, nationality or accent as a proxy.</p>
      <div className="space-y-3">
        {EN_SECTIONS.map(([k, lbl, mx]) => (
          <div key={k} className="flex items-center gap-3">
            <div className="flex-1"><div className="text-xs font-semibold text-slate-800">{lbl}</div><div className="text-[10px] text-slate-500">Max {mx}</div></div>
            <input type="number" min={0} max={mx} data-testid={`en-${k}`} value={scores[k]} onChange={e=>setScores({...scores, [k]: Math.min(mx, Math.max(0, parseInt(e.target.value)||0))})} className="w-20 h-9 px-3 rounded border border-slate-300 text-sm font-mono"/>
          </div>
        ))}
      </div>
      <div className="mt-4 p-3 rounded-md bg-slate-50 border border-slate-200 text-sm flex justify-between">
        <span className="font-semibold text-slate-800">Total</span>
        <span className="font-mono font-bold text-lg text-slate-900">{total} / {max}</span>
      </div>
      <button data-testid="en-submit" onClick={submit} disabled={busy} className="mt-4 h-10 px-5 rounded-md bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin"/>} Submit self-assessment</button>
    </div>
  );
};

function MediaAuditions({me, media, auditionQs, onUpload, uploadIntro, backendUrl, busy, onReload}) {
  const [subtab, setSubtab] = useState("intro");
  const [selectedFamily, setSelectedFamily] = useState(ROLE_FAMILIES[0]);
  const [playing, setPlaying] = useState(null);

  const mediaByQ = media.reduce((acc,m)=>{ if (m.question_id) acc[m.question_id]=m; return acc; },{});
  const mediaByType = media.reduce((acc,m)=>{ (acc[m.media_type] = acc[m.media_type]||[]).push(m); return acc; },{});

  const interviewQs = auditionQs.filter(q => q.media_type === "INTERVIEW_AUDIO_RESPONSE" && (!q.role_family || q.role_family === selectedFamily));
  const auditionQsForFamily = auditionQs.filter(q => q.role_family === selectedFamily && q.media_type !== "INTERVIEW_AUDIO_RESPONSE");

  const handleUpload = async (file, mtype, opts) => {
    if (!file) return;
    // Basic size guard
    const isVideo = mtype.includes("VIDEO");
    if (isVideo && file.size > 60*1024*1024) { toast.error("Video too large (max 60 MB)"); return; }
    if (!isVideo && file.size > 15*1024*1024) { toast.error("File too large (max 15 MB)"); return; }
    await onUpload(file, mtype, opts);
  };

  const SUBTABS = [["intro","Video Introduction"],["interview","Interview Responses"],["audition","Role Auditions"],["sample","Work Samples"]];

  return (
    <div>
      <div className="mb-4 bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-2"><Video size={14} className="text-sky-600"/><h3 className="text-sm font-semibold text-slate-900">Media & Auditions</h3></div>
        <p className="text-xs text-slate-500">Submit your professional media so recruiters and employers can experience your communication and skills. All uploads are secure, streamed on demand, and never shared without your recruiter's approval.</p>
      </div>

      <div className="mb-4 flex gap-1 flex-wrap">
        {SUBTABS.map(([id,lbl])=>(
          <button key={id} data-testid={`msub-${id}`} onClick={()=>setSubtab(id)} className={`px-3 py-1.5 text-xs font-semibold rounded-md border ${subtab===id?"bg-slate-900 text-white border-slate-900":"border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
        ))}
      </div>

      {subtab==="intro" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3"><Video size={16} className="text-sky-600"/><h3 className="font-semibold text-slate-900">Video introduction (required for Qualified & Premier)</h3></div>
          <ol className="text-xs text-slate-600 mb-4 list-decimal ml-5 space-y-1">
            <li>Introduce yourself and describe the type of opportunity you are seeking.</li>
            <li>What work are you most passionate about and why?</li>
            <li>What are you exceptionally good at?</li>
            <li>Which professional accomplishment are you most proud of?</li>
            <li>What unique value do you bring to an employer?</li>
            <li>Why should a company choose you over another qualified candidate?</li>
          </ol>
          <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold cursor-pointer">
            <Upload size={13}/> {me.intro_video_url?"Replace video":"Upload video"}
            <input type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" onChange={uploadIntro} data-testid="video-upload" className="hidden"/>
          </label>
          <p className="text-[11px] text-slate-500 mt-2">Max 60MB. Supported: MP4, WebM, MOV.</p>
          {busy && <div className="mt-3 text-xs flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Uploading...</div>}
          {me.intro_video_url && (
            <div className="mt-5">
              <div className="text-xs text-emerald-700 mb-2 flex items-center gap-1.5"><Play size={12}/> Video submitted — under recruiter review</div>
              <video controls className="w-full max-w-lg rounded-md border border-slate-200" src={`${backendUrl}${me.intro_video_url}`} data-testid="video-preview" crossOrigin="use-credentials"/>
            </div>
          )}
        </div>
      )}

      {(subtab==="interview" || subtab==="audition") && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500 font-semibold uppercase tracking-widest">Role family</label>
          <select data-testid="role-family-select" value={selectedFamily} onChange={e=>setSelectedFamily(e.target.value)} className="h-9 px-3 rounded border border-slate-300 text-sm">
            {ROLE_FAMILIES.map(f=><option key={f}>{f}</option>)}
          </select>
          <button onClick={onReload} className="ml-auto text-xs inline-flex items-center gap-1 text-slate-500"><RefreshCw size={11}/> Refresh</button>
        </div>
      )}

      {subtab==="interview" && (
        <div className="space-y-3">
          {interviewQs.length===0 ? <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg p-6">No interview questions assigned for this role family yet.</div> :
            interviewQs.map(q => (
              <QuestionCard key={q.id} q={q} existing={mediaByQ[q.id]} playing={playing} setPlaying={setPlaying} onUpload={file=>handleUpload(file, "INTERVIEW_AUDIO_RESPONSE", {question_id:q.id, role_family:selectedFamily})} accept=".mp3,.m4a,.wav,.webm,.ogg,audio/*"/>
            ))}
        </div>
      )}

      {subtab==="audition" && (
        <div className="space-y-3">
          {auditionQsForFamily.length===0 ? <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg p-6">No role auditions assigned for this role family yet.</div> :
            auditionQsForFamily.map(q => (
              <QuestionCard key={q.id} q={q} existing={mediaByQ[q.id]} playing={playing} setPlaying={setPlaying}
                            onUpload={file=>handleUpload(file, q.media_type, {question_id:q.id, role_family:selectedFamily, title:q.prompt.slice(0,80)})}
                            accept={q.media_type==="WORK_SAMPLE_MEDIA"?".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.jpg,.jpeg,.png,image/*,application/*":q.media_type.includes("VIDEO")?".mp4,.webm,.mov,video/*":".mp3,.m4a,.wav,.webm,.ogg,audio/*"}/>
            ))}
        </div>
      )}

      {subtab==="sample" && (
        <WorkSampleUploader existing={mediaByType.WORK_SAMPLE_MEDIA||[]} onUpload={file=>handleUpload(file, "WORK_SAMPLE_MEDIA", {title: file.name})} onReload={onReload}/>
      )}
    </div>
  );
}

function QuestionCard({q, existing, playing, setPlaying, onUpload, accept}) {
  const active = existing && playing === existing.id;
  const isVideo = q.media_type.includes("VIDEO");
  const inputId = `q-input-${q.id}`;
  const supportsRecording = !isVideo && q.media_type !== "WORK_SAMPLE_MEDIA";
  const [rec, setRec] = useState({state:"idle", ms:0, url:null, blob:null});
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" :
                    MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? {mimeType:mime} : undefined);
      chunksRef.current = [];
      mr.ondataavailable = e => e.data.size>0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, {type: mr.mimeType || "audio/webm"});
        const url = URL.createObjectURL(blob);
        setRec(r => ({...r, state:"recorded", url, blob}));
        stream.getTracks().forEach(t=>t.stop());
      };
      mr.start();
      mediaRecRef.current = mr;
      const started = Date.now();
      setRec({state:"recording", ms:0, url:null, blob:null});
      timerRef.current = setInterval(()=>setRec(r=>({...r, ms: Date.now()-started})), 200);
    } catch (e) {
      toast.error(e.name==="NotAllowedError" ? "Microphone permission denied. Please enable it in browser settings." : "Cannot access microphone");
    }
  };
  const stopRecording = () => {
    try { mediaRecRef.current?.stop(); } catch {}
    clearInterval(timerRef.current);
  };
  const discardRecording = () => {
    if (rec.url) URL.revokeObjectURL(rec.url);
    setRec({state:"idle", ms:0, url:null, blob:null});
  };
  const submitRecording = async () => {
    if (!rec.blob) return;
    const ext = (rec.blob.type.includes("webm") ? ".webm" : rec.blob.type.includes("mp4") ? ".m4a" : ".webm");
    const file = new File([rec.blob], `recording-${q.id}${ext}`, {type: rec.blob.type});
    await onUpload(file);
    discardRecording();
  };
  useEffect(()=>()=>{ clearInterval(timerRef.current); rec.url && URL.revokeObjectURL(rec.url); }, []); // eslint-disable-line
  const secs = Math.floor(rec.ms/1000);
  const mm = String(Math.floor(secs/60)).padStart(2,"0"), ss = String(secs%60).padStart(2,"0");

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`q-card-${q.id}`}>
      <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold mb-1">{q.media_type.replace(/_/g," ").toLowerCase()}{q.required?" · required":""}</div>
      <div className="text-sm text-slate-900 font-medium mb-3">{q.prompt}</div>
      {supportsRecording && (
        <div className="mb-3 p-3 rounded-md border border-sky-200 bg-sky-50/50">
          <div className="text-[10px] uppercase tracking-widest text-sky-800 font-semibold mb-2 flex items-center gap-1.5"><Mic size={11}/> Record in browser</div>
          {rec.state==="idle" && (
            <button data-testid={`q-record-${q.id}`} onClick={startRecording} className="h-9 px-3 rounded-md bg-sky-600 text-white text-xs font-semibold inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-white"/> Start recording
            </button>
          )}
          {rec.state==="recording" && (
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-mono font-bold text-red-600"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"/> {mm}:{ss}</span>
              <button data-testid={`q-stop-${q.id}`} onClick={stopRecording} className="h-9 px-3 rounded-md bg-slate-900 text-white text-xs font-semibold">Stop</button>
            </div>
          )}
          {rec.state==="recorded" && rec.url && (
            <div className="space-y-2">
              <audio controls src={rec.url} className="w-full max-w-sm" data-testid={`q-preview-${q.id}`}/>
              <div className="flex gap-2">
                <button data-testid={`q-submit-${q.id}`} onClick={submitRecording} className="h-9 px-3 rounded-md bg-emerald-600 text-white text-xs font-semibold">Submit</button>
                <button data-testid={`q-rerecord-${q.id}`} onClick={()=>{discardRecording(); startRecording();}} className="h-9 px-3 rounded-md border border-slate-300 text-xs font-semibold">Re-record</button>
                <button onClick={discardRecording} className="h-9 px-3 rounded-md border border-slate-300 text-xs font-semibold">Discard</button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <label htmlFor={inputId} className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-slate-900 text-white text-xs font-semibold cursor-pointer">
          <Upload size={12}/> {existing?"Upload replacement file":"Upload file (fallback)"}
          <input id={inputId} type="file" accept={accept} data-testid={`q-upload-${q.id}`} onChange={e=>{const f=e.target.files?.[0]; e.target.value=""; f && onUpload(f);}} className="hidden"/>
        </label>
        {existing && (
          <>
            <button onClick={()=>setPlaying(active?null:existing.id)} className="h-9 px-3 rounded-md border border-slate-300 text-xs font-semibold inline-flex items-center gap-1" data-testid={`q-play-${q.id}`}><Play size={11}/>{active?"Hide":"Play"}</button>
            <span className={`inline-flex text-[9px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${STATUS_TONE[existing.approval_status]||STATUS_TONE.Submitted}`}>{existing.approval_status || "Submitted"}</span>
          </>
        )}
      </div>
      {active && (
        <div className="mt-3">
          {isVideo ? (
            <video controls className="w-full max-w-lg rounded border border-slate-200" src={`${API_BASE}/media/${existing.id}/stream`} crossOrigin="use-credentials"/>
          ) : q.media_type==="WORK_SAMPLE_MEDIA" ? (
            <a href={`${API_BASE}/media/${existing.id}/stream`} target="_blank" rel="noreferrer" className="text-xs text-sky-700 font-semibold">Open uploaded file</a>
          ) : (
            <audio controls className="w-full max-w-lg" src={`${API_BASE}/media/${existing.id}/stream`} crossOrigin="use-credentials"/>
          )}
        </div>
      )}
    </div>
  );
}

function WorkSampleUploader({existing, onUpload, onReload}){
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-3"><FileText size={16} className="text-sky-600"/><h3 className="font-semibold text-slate-900">Work samples</h3></div>
      <p className="text-xs text-slate-500 mb-4">Attach documents, PDFs, spreadsheets, or images that represent your best work. Employers only see samples your recruiter has approved.</p>
      <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold cursor-pointer">
        <Upload size={13}/> Upload work sample
        <input type="file" data-testid="worksample-upload" onChange={e=>{const f=e.target.files?.[0]; e.target.value=""; f && onUpload(f);}} className="hidden"/>
      </label>
      <div className="mt-4 space-y-2">
        {existing.length===0 ? <div className="text-xs text-slate-500">No samples uploaded.</div> :
          existing.map(m => (
            <div key={m.id} className="border border-slate-200 rounded p-3 text-xs flex items-center justify-between">
              <div><div className="font-semibold text-slate-900">{m.title || "Work sample"}</div><div className="text-slate-500">Uploaded {new Date(m.submitted_at).toLocaleDateString()} · {Math.round((m.size||0)/1024)} KB</div></div>
              <a href={`${API_BASE}/media/${m.id}/stream`} target="_blank" rel="noreferrer" className="text-sky-700 font-semibold">Open</a>
            </div>
          ))}
      </div>
    </div>
  );
}
